import { Link } from '@tanstack/react-router';
import {
  ChatMarkdown,
  ChatMessage as ChatMessageBubble,
  ChatMessageList,
  ChatRecapCard,
  chatCardLinkVariants,
} from '@wundo/ui';
import type { OnboardingNudgeMessageSource } from '@wundo/shared/onboardingNudge.ts';
import { m } from '@/paraglide/messages.js';
import type { ChatPageNudge } from '@/lib/chatPageOffer.ts';
import type { ConversationalOpening } from '@/lib/chatPageOpening.ts';
import { ChatNudge } from '@/components/ChatNudge.tsx';

export interface ChatEmptyDrawerProps {
  /** The page's opening while it is being answered, or null. */
  opening: ConversationalOpening | null;
  /** The recommendation to draw where there is no opening, or null. */
  nudge: ChatPageNudge | null;
  userId: string | undefined;
  /** Whether the nudge's button can still be pressed. */
  disabled: boolean;
  onSend: (text: string, source?: OnboardingNudgeMessageSource) => void;
  onNudgeSettled: () => void;
}

/**
 * The one thing that stands in an empty drawer: what the page opens with — the
 * question about a meeting Wundo can neither prepare nor write up on its own,
 * or the summary of the previous conversation — or, failing both, the one
 * thing worth recommending to somebody still finding their way.
 *
 * Drawn, not sent and not stored: saying something is what creates the
 * conversation, and the answer comes back with these same words in it, written
 * by the server.
 */
export function ChatEmptyDrawer({
  opening,
  nudge,
  userId,
  disabled,
  onSend,
  onNudgeSettled,
}: ChatEmptyDrawerProps) {
  if (opening) {
    return <ChatOpeningMessage opening={opening} />;
  }

  if (!nudge) {
    return null;
  }

  /*
   * Not a bubble: Wundo has not said this, the page has. So it stands on its
   * own in the empty drawer, with its own way out — everything else in here was
   * asked for, and this was not.
   */
  return (
    <ChatNudge
      nudge={nudge}
      userId={userId}
      disabled={disabled}
      onSend={onSend}
      onSettled={onNudgeSettled}
    />
  );
}

/* The opening drawn as Wundo's own bubble: a line the server wrote, or a recap card. */
function ChatOpeningMessage({ opening }: { opening: ConversationalOpening }) {
  return (
    <ChatMessageList>
      <ChatMessageBubble author='wundo' authorLabel={m.chat_author_wundo()}>
        {opening.kind !== 'recap' ? (
          <ChatMarkdown>{opening.opener}</ChatMarkdown>
        ) : (
          <ChatRecapCard
            title={opening.recap.title}
            text={opening.recap.text}
            /*
             * The verslag it was read from is a page of this same app, so this
             * stays inside the router: following it must not throw the drawer,
             * and the conversation in it, away.
             */
            link={
              <Link
                to='/meetings/$meetingId'
                params={{ meetingId: opening.recap.linkMeetingId }}
                search={{ tab: 'report' }}
                className={chatCardLinkVariants()}
              >
                {opening.recap.linkText}
              </Link>
            }
          />
        )}
      </ChatMessageBubble>
    </ChatMessageList>
  );
}
